import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import GoogleServicesConnection from '../../components/GoogleServicesConnection';
import { 
  getAccountScopedUserProfile,
  updateAccountScopedUserProfile,
  getEffectiveUserId
} from '../../services/accountScopedDataService';
import { addScopeChangeListener } from '../../services/authorizationService';
import { 
  User, 
  Shield, 
  Bell, 
  Palette, 
  Globe, 
  SignOut,
  Trash,
  CreditCard,
  WarningCircle
} from '@phosphor-icons/react';
import SubscriptionStatus from '../../components/SubscriptionStatus';
import SocialMediaConnections from '../../components/SocialMediaConnections';
import PaymentModal from '../../components/PaymentModal';
import GoogleBusinessProfileLocations from '../../components/GoogleBusinessProfileLocations';

export default function AccountSettings() {
  const { currentUser, signOut } = useAuth();
  const { canAccessDashboard, startSubscription } = useSubscription();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [, setEffectiveUserId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'google', label: 'Google Services', icon: Globe },
    { id: 'social', label: 'Social Media', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Palette }
  ];

  // Load account-scoped profile data
  const loadProfileData = async () => {
    try {
      setIsLoading(true);
      const data = await getAccountScopedUserProfile();
      setProfileData(data);
    } catch (error) {
      console.error('Error loading profile data:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setIsLoading(false);
    }
  };

  // Track effective user ID changes and load profile data
  useEffect(() => {
    const newEffectiveUserId = getEffectiveUserId();
    setEffectiveUserId(newEffectiveUserId);
    
    if (currentUser && newEffectiveUserId) {
      loadProfileData();
    }
  }, [currentUser]); // Re-run when currentUser changes

  // Listen for account scope changes
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed:', scopeData);
      const newEffectiveUserId = getEffectiveUserId();
      setEffectiveUserId(newEffectiveUserId);
      
      if (currentUser && newEffectiveUserId) {
        loadProfileData();
      }
    });

    return unsubscribe;
  }, [currentUser]);

  const handleSaveProfile = async (formData) => {
    try {
      setIsSaving(true);
      await updateAccountScopedUserProfile(formData);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      // Reload profile data
      const data = await getAccountScopedUserProfile();
      setProfileData(data);
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setIsSaving(false);
    }
  };



  const handleDeleteAccount = () => {
    // Handle account deletion
    console.log('Deleting account...');
  };

  const handlePaymentSuccess = async (paymentMethod) => {
    setIsProcessingPayment(true);
    try {
      // For renewing subscriptions, we'll use the existing startSubscription function
      // This will create a new subscription for the user
      await startSubscription(paymentMethod);
      setShowPaymentModal(false);
      // The subscription context will refresh automatically
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    console.error('Payment error:', errorMessage);
    setIsProcessingPayment(false);
  };

  const handleRenewSubscription = () => {
    setShowPaymentModal(true);
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account preferences and connections</p>
          {profileData?.isAdminScoped && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-blue-800">
                  <strong>Operating as:</strong> {profileData?.name || profileData?.displayName || 'Client Account'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Subscription Renewal Message for Suspended Accounts */}
        {!canAccessDashboard() && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <WarningCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Subscription Expired</h3>
                <p className="text-sm text-red-700 mt-1">
                  Your subscription has expired. Please renew your subscription to access the Shift Healthcare Platform.
                </p>
                <div className="mt-3">
                  <button
                    onClick={handleRenewSubscription}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    Renew Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64">
            <nav className="bg-white rounded-lg shadow p-4">
              <ul className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <li key={tab.id}>
                      <button
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>
                
                {/* Message */}
                {message.text && (
                  <div className={`mb-4 p-3 rounded-md ${
                    message.type === 'success' ? 'bg-green-100 text-green-800' :
                    message.type === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {message.text}
                  </div>
                )}

                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading profile...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                        {profileData?.photoURL ? (
                          <img 
                            src={profileData.photoURL} 
                            alt="Profile" 
                            className="w-20 h-20 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-10 h-10 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {profileData?.displayName || profileData?.name || 'User'}
                        </h3>
                        <p className="text-gray-600">{profileData?.email}</p>
                        <p className="text-sm text-gray-500">
                          {profileData?.isAdminScoped ? 'Client Account' : 'Personal Account'}
                        </p>
                      </div>
                    </div>

                    {/* Profile Form */}
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      handleSaveProfile({
                        name: formData.get('name'),
                        description: formData.get('description')
                      });
                    }}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {profileData?.isAdminScoped ? 'Account Name' : 'Display Name'}
                          </label>
                          <input
                            type="text"
                            name="name"
                            defaultValue={profileData?.displayName || profileData?.name || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={profileData?.isAdminScoped ? 'Enter account name' : 'Enter your display name'}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                          </label>
                          <input
                            type="email"
                            defaultValue={profileData?.email || ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>

                        {profileData?.isAdminScoped && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Account Description
                            </label>
                            <textarea
                              name="description"
                              defaultValue={profileData?.description || ''}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter account description"
                            />
                          </div>
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end">
                        <button 
                          type="submit"
                          disabled={isSaving}
                          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <SubscriptionStatus showDetails={true} />
              </div>
            )}

            {/* Google Services Tab */}
            {activeTab === 'google' && (
              <div className="space-y-6">
                <GoogleServicesConnection />
                <GoogleBusinessProfileLocations />
              </div>
            )}

            {/* Social Media Tab */}
            {activeTab === 'social' && (
              <div className="space-y-6">
                <SocialMediaConnections />
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Security Settings</h2>
                
                <div className="space-y-6">
                  {/* Password Change */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter current password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Confirm new password"
                        />
                      </div>
                      <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                        Update Password
                      </button>
                    </div>
                  </div>

                  {/* Two-Factor Authentication */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Two-Factor Authentication</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600">Add an extra layer of security to your account</p>
                        <p className="text-sm text-gray-500 mt-1">Currently disabled</p>
                      </div>
                      <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                        Enable 2FA
                      </button>
                    </div>
                  </div>

                  {/* Account Deletion */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h3>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-red-900">Delete Account</h4>
                          <p className="text-sm text-red-700 mt-1">
                            Once you delete your account, there is no going back. Please be certain.
                          </p>
                        </div>
                        <button
                          onClick={handleDeleteAccount}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                        >
                          <Trash className="w-4 h-4" />
                          <span>Delete Account</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Preferences</h2>
                
                <div className="space-y-6">
                  {/* Notifications */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                        <span className="ml-2 text-gray-700">Email notifications</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                        <span className="ml-2 text-gray-700">Push notifications</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="ml-2 text-gray-700">SMS notifications</span>
                      </label>
                    </div>
                  </div>

                  {/* Language */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Language & Region</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Language
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option>English</option>
                          <option>Spanish</option>
                          <option>French</option>
                          <option>German</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Time Zone
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option>UTC-8 (Pacific Time)</option>
                          <option>UTC-7 (Mountain Time)</option>
                          <option>UTC-6 (Central Time)</option>
                          <option>UTC-5 (Eastern Time)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end border-t pt-6">
                    <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sign Out Button */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={signOut}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
          >
            <SignOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        loading={isProcessingPayment}
        disabled={isProcessingPayment}
        title="Renew Subscription"
        description="Complete your payment to renew your Shift Healthcare Pro subscription."
      />
    </div>
  );
}
